import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

function ClientDetail() {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, [clientId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [clientData, jobsData] = await Promise.all([
        api.getClient(clientId),
        api.getJobs(clientId),  // Filter jobs by client_id on the server
      ]);
      setClient(clientData);
      setJobs(jobsData);
    } catch (err) {
      setError(err.message || 'Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = '$') => {
    if (!amount) return `${currency}0.00`;
    return `${currency}${parseFloat(amount).toFixed(2)}`;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return statusStyles[status] || 'bg-slate-100 text-slate-700';
  };

  const formatAddress = () => {
    if (!client) return null;
    const parts = [];
    if (client.address_street) parts.push(client.address_street);
    if (client.address_street2) parts.push(client.address_street2);
    const cityStateZip = [
      client.address_city,
      client.address_state,
      client.address_zip,
    ].filter(Boolean).join(', ');
    if (cityStateZip) parts.push(cityStateZip);
    return parts.length > 0 ? parts : null;
  };

  if (loading) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading client...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-xl text-center">
            <i className="fas fa-exclamation-circle text-3xl mb-3"></i>
            <p>{error}</p>
            <Link to="/clients" className="mt-4 inline-block text-primary hover:underline">
              Back to Clients
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-600">Client not found</p>
          <Link to="/clients" className="text-primary hover:underline">
            Back to Clients
          </Link>
        </div>
      </div>
    );
  }

  const address = formatAddress();

  return (
    <div className="py-12 px-6 bg-background min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Back Link */}
        <Link
          to="/clients"
          className="inline-flex items-center text-slate-600 hover:text-primary mb-6 transition"
        >
          <i className="fas fa-arrow-left mr-2"></i>
          Back to Clients
        </Link>

        {/* Client Info Card */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 mb-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-primary mb-2">
                {client.name}
              </h1>
              <div className="flex flex-wrap gap-4 text-slate-600">
                <span>
                  <i className="fas fa-envelope mr-2 text-slate-400"></i>
                  <a href={`mailto:${client.email}`} className="hover:text-primary">
                    {client.email}
                  </a>
                </span>
                {client.phone && (
                  <span>
                    <i className="fas fa-phone mr-2 text-slate-400"></i>
                    <a href={`tel:${client.phone}`} className="hover:text-primary">
                      {client.phone}
                    </a>
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Jobs</p>
              <p className="text-2xl font-bold text-primary">{jobs.length}</p>
            </div>
          </div>

          {/* Address */}
          {address && (
            <div className="pt-4 border-t border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Address</p>
              <div className="text-slate-800">
                {address.map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {client.notes && (
            <div className="pt-4 mt-4 border-t border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Notes</p>
              <p className="text-slate-800 whitespace-pre-wrap">{client.notes}</p>
            </div>
          )}
        </div>

        {/* Jobs Section */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-primary">Jobs</h2>
          <div className="flex items-center gap-3">
            <Link
              to={`/jobs?newJob=true&clientId=${clientId}`}
              className="inline-flex items-center gap-2 bg-secondary text-white px-4 py-2 rounded-lg font-medium hover:bg-orange-600 transition text-sm"
            >
              <i className="fas fa-plus"></i>
              New Job
            </Link>
            <Link
              to="/jobs"
              className="text-secondary hover:underline text-sm font-medium"
            >
              View All Jobs
            </Link>
          </div>
        </div>

        {jobs.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl shadow-sm border border-slate-200 text-center">
            <i className="fas fa-briefcase text-5xl text-slate-300 mb-4"></i>
            <h3 className="text-lg font-bold text-slate-700 mb-2">No Jobs Yet</h3>
            <p className="text-slate-600">
              No jobs have been created for this client.
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {jobs.map((job) => (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md hover:border-secondary/30 transition block"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-primary">{job.title}</h3>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(job.status)}`}>
                        {job.status}
                      </span>
                    </div>
                    {job.address && (
                      <p className="text-sm text-slate-500">
                        <i className="fas fa-map-marker-alt mr-2 text-slate-400"></i>
                        {job.address}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-6">
                    {job.initial_value && (
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Value</p>
                        <p className="text-lg font-bold text-primary">
                          {formatCurrency(job.initial_value)}
                        </p>
                      </div>
                    )}
                    {job.scheduled_date && (
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Scheduled</p>
                        <p className="text-sm font-medium text-slate-700">
                          {new Date(job.scheduled_date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    <i className="fas fa-chevron-right text-slate-400"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientDetail;
